<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { ChevronRight } from "lucide-svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Goal from "$app/Pages/Plans/Onboarding/components/Goal.svelte"
  import { appStore } from "$app/store.svelte"

  let completed = $state(true)
  let loaded = $state(false)
  let saving = $state(false)

  let long_term_goal = $state({} as GoalType)
  let annual_goal = $state({} as GoalType)

  let errors: Record<string, any> = $state({})

  const initialForm = {
    id: null,
    name: "",
    type: "QuarterlyGoal",
    description: "",
    obstacles_text: "",
    assignee_type: "Department",
    assignee_id: 0,
    status: "on_track",
    parent_goal_id: 0,
    parent_goal_type: "AnnualGoal"
  }

  let form = $state({ ...initialForm })

  async function getGoals(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=quarterly_goal`
      )

      long_term_goal = resp.data.long_term_goal
      annual_goal = resp.data.annual_goal

      form.id = resp.data.quarterly_goal.id
      form.name = resp.data.quarterly_goal.name

      if (!resp.data.quarterly_goal.assignee_id) {
        form.assignee_id = annual_goal.assignee_id
      }
      form.parent_goal_id = annual_goal.id
      form.description = resp.data.quarterly_goal.description
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your annual goal", "error")
    } finally {
      loaded = true
    }
  }

  async function nextStep(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.plan.id}/create_goal.json`,
        {
          goal: form
        }
      )

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
      appStore.onboarding = resp.data.onboarding
    } catch (e: any) {
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  onMount(() => {
    getGoals()
  })
</script>

<h2 class="text-2xl text-center text-white">Guided Initial Goals Set Up</h2>
<h3 class="mt-2 text-xl font-bold text-center text-quarterly-goal">
  <span class="text-2xl text-bold mr-1">2. </span>
  Add a Quarterly Goal
</h3>

<div class="max-w-3xl mx-auto">
  <p class="mt-6 text-lg text-white">
    Quarterly Goals detail what you want to accomplish in a 90-day period. Each
    quarterly goal is linked to an annual goal.
  </p>

  {#if loaded}
    <div class="flex items-center pt-6">
      <p class="text-2xl text-white">
        You can see the heriarchy of your goals below.
      </p>
    </div>

    <div class="scale-75">
      <ul class="hierarchy" style="transform: translateX(-100px)">
        <li>
          <Goal name={long_term_goal.name} goalType="long_term_goal" />

          <ul class="ml-8">
            <li>
              <Goal name={annual_goal.name} goalType="annual_goal" />
              <ul class="ml-8">
                <li>
                  <Goal name={form.name} goalType="quarterly_goal" />
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    {#if form.parent_goal_id !== null}
      <div class="ml-6">
        <div class="space-y-4 mt-2 max-w-xl">
          <div>
            <div class="mt-6 grid grid-cols-2 gap-y-3 gap-x-4">
              <div class="col-span-2">
                <label for="name" class="text-white text-lg">Name </label>
                <div class="mt-2 input-cont" class:error={errors.name}>
                  <input
                    type="text"
                    bind:value={form.name}
                    placeholder="Goal name"
                    class="input input-lg"
                  />
                  <div class="msg">{errors.name}</div>
                </div>
              </div>
              <div class="col-span-2" class:error={errors.assignee_id}>
                <label for="assignee_id" class="text-white text-lg"
                  >Assignee</label
                >
                <div class="mt-1 input-cont" class:error={errors.assignee}>
                  <select
                    class="input input-lg"
                    id="goal_assignee_id"
                    bind:value={form.assignee_id}
                  >
                    {#if appStore.departments.length}
                      {#each appStore.departments as dept (dept.id)}
                        <option value={dept.id}>{dept.name}</option>
                      {/each}
                    {:else}
                      <option value="">No Departments</option>
                    {/if}
                  </select>
                  <div class="msg">{errors.assignee}</div>
                </div>
              </div>

              <div class="mt-6 flex justify-start gap-x-5">
                <Button
                  onclick={nextStep}
                  disabled={!completed}
                  processing={saving}
                  class="btn btn-rounded success btn-big flex items-center gap-1"
                >
                  <span class="font-light text-core"> CREATE GOAL </span>
                  <ChevronRight class="size-6 text-core" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</div>
